// src/ClientsView.jsx
import React, { useMemo, useState } from "react";

const INSURANCES = [
  "Aetna",
  "Highmark",
  "Capital Blue Cross",
  "UPMC",
  "Geisinger",
  "Optum",
  "United",
  "Cigna",
  "Self-pay",
  "Other",
];

const MODALITIES = [
  { value: "either", label: "Either (in-person or virtual)" },
  { value: "in_person", label: "In-person" },
  { value: "virtual", label: "Virtual" },
  { value: "hybrid", label: "Hybrid" },
];

const LOCATIONS = ["Myerstown", "Lebanon", "Lancaster", "Harrisburg", "Other"];

const GENDER_PREFS = [
  { value: "no_preference", label: "No preference" },
  { value: "female", label: "Female clinician" },
  { value: "male", label: "Male clinician" },
  { value: "nonbinary", label: "Non-binary clinician" },
];

function fmtDate(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function safeArr(v) {
  return Array.isArray(v) ? v : [];
}

function getAssignmentForClient(assignments, clientId) {
  const arr = safeArr(assignments);
  return arr.find((a) => a?.client_id === clientId) || null;
}

function clinicianLabel(cl) {
  if (!cl) return "—";
  const fn = cl.first_name || "";
  const ln = cl.last_name || "";
  return `${fn} ${ln}`.trim() || "—";
}

export default function ClientsView({ clients, clinicians, assignments, api }) {
  const _clients = safeArr(clients);
  const _clinicians = safeArr(clinicians);
  const _assignments = safeArr(assignments);

  const [selectedId, setSelectedId] = useState(_clients[0]?.id ?? null);
  const selected = useMemo(
    () => _clients.find((c) => c.id === selectedId) || null,
    [_clients, selectedId]
  );

  // --- Create form state (Option A: remove age_group) ---
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    insurance: "Aetna",
    insurance_other_notes: "",
    preferred_modality: "either",
    preferred_location: "Myerstown",
    gender_preference: "no_preference",
    availability_notes: "",
    presenting_concerns: "",
    sliding_scale_ok: false,
    insurance_verified: false,
    insurance_verified_notes: "",
  });

  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const assignment = useMemo(
    () => (selected ? getAssignmentForClient(_assignments, selected.id) : null),
    [_assignments, selected]
  );

  const assignedClinician = useMemo(() => {
    if (!assignment?.clinician_id) return null;
    return _clinicians.find((c) => c.id === assignment.clinician_id) || null;
  }, [_clinicians, assignment]);

  const handleFormChange = (key) => (e) => {
    const v =
      e?.target?.type === "checkbox" ? !!e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: v }));
  };

  const resetForm = () => {
    setForm({
      first_name: "",
      last_name: "",
      insurance: "Aetna",
      insurance_other_notes: "",
      preferred_modality: "either",
      preferred_location: "Myerstown",
      gender_preference: "no_preference",
      availability_notes: "",
      presenting_concerns: "",
      sliding_scale_ok: false,
      insurance_verified: false,
      insurance_verified_notes: "",
    });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setErrorMsg("");

    if (!form.first_name.trim() || !form.last_name.trim()) {
      setErrorMsg("Please enter first and last name.");
      return;
    }

    setBusy(true);
    try {
      const payload = {
        ...form,
        // If virtual, store location as "virtual"
        preferred_location:
          form.preferred_modality === "virtual"
            ? "virtual"
            : form.preferred_location,
        // Use ISO timestamp for timestamptz columns
        insurance_verified_at: form.insurance_verified
          ? new Date().toISOString()
          : null,
      };

      const created = await api.addClient(payload);
      if (created?.id) setSelectedId(created.id);

      resetForm();
    } catch (err) {
      console.error("addClient error:", err);
      // Helpful Supabase/PostgREST messages come through here
      setErrorMsg(err?.message || "Failed to add client.");
    } finally {
      setBusy(false);
    }
  };

  const updateSelected = async (patch) => {
    if (!selected?.id) return;
    setBusy(true);
    setErrorMsg("");
    try {
      await api.updateClient(selected.id, patch);
    } catch (err) {
      console.error("updateClient error:", err);
      setErrorMsg(err?.message || "Failed to update client.");
    } finally {
      setBusy(false);
    }
  };

  const deleteSelected = async () => {
    if (!selected?.id) return;
    const ok = window.confirm(
      "Delete this client? This also removes any assignment."
    );
    if (!ok) return;

    setBusy(true);
    setErrorMsg("");
    try {
      await api.deleteClient(selected.id);
      const remaining = _clients.filter((c) => c.id !== selected.id);
      setSelectedId(remaining[0]?.id ?? null);
    } catch (err) {
      console.error("deleteClient error:", err);
      setErrorMsg(err?.message || "Failed to delete client.");
    } finally {
      setBusy(false);
    }
  };

  const onAssignChange = async (e) => {
    if (!selected?.id) return;
    const clinicianId = e.target.value || null;
    setBusy(true);
    setErrorMsg("");
    try {
      if (!clinicianId) {
        await api.unassignClient(selected.id);
      } else {
        await api.assignClient(selected.id, clinicianId);
      }
    } catch (err) {
      console.error("assignment error:", err);
      setErrorMsg(err?.message || "Failed to update assignment.");
    } finally {
      setBusy(false);
    }
  };

  // Nicer-looking buttons without touching CSS
  const primaryBtnStyle = {
    borderRadius: 16,
    padding: "12px 16px",
    fontWeight: 900,
  };

  const dangerBtnStyle = {
    borderRadius: 16,
    padding: "10px 14px",
    fontWeight: 900,
  };

  return (
    <div className="cp-bg">
      <div className="cp-container">
        <div className="cp-demoBanner">
          Demo prototype — do not enter real patient data.
        </div>

        {/* Add Client */}
        <div className="cp-card alt" style={{ marginBottom: 14 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 10,
            }}
          >
            <h3 style={{ margin: 0 }}>Add client</h3>
            <div className="cp-pill">Org-scoped</div>
          </div>

          {errorMsg ? (
            <div
              className="cp-note"
              style={{
                marginBottom: 10,
                borderColor: "rgba(239,68,68,.35)",
                background: "rgba(239,68,68,.06)",
              }}
            >
              <div style={{ fontWeight: 900, marginBottom: 4 }}>Couldn’t save</div>
              <div style={{ whiteSpace: "pre-wrap" }}>{errorMsg}</div>
            </div>
          ) : null}

          <form className="cp-form" onSubmit={submit}>
            <input
              className="cp-input"
              placeholder="First name"
              value={form.first_name}
              onChange={handleFormChange("first_name")}
              disabled={busy}
            />

            <input
              className="cp-input"
              placeholder="Last name"
              value={form.last_name}
              onChange={handleFormChange("last_name")}
              disabled={busy}
            />

            <select
              className="cp-select"
              value={form.insurance}
              onChange={handleFormChange("insurance")}
              disabled={busy}
            >
              {INSURANCES.map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </select>

            <select
              className="cp-select"
              value={form.preferred_modality}
              onChange={handleFormChange("preferred_modality")}
              disabled={busy}
            >
              {MODALITIES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>

            <select
              className="cp-select"
              value={form.preferred_location}
              onChange={handleFormChange("preferred_location")}
              disabled={busy || form.preferred_modality === "virtual"}
              title={
                form.preferred_modality === "virtual"
                  ? "Location is set to virtual when modality is virtual"
                  : ""
              }
            >
              {LOCATIONS.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>

            <input
              className="cp-input"
              placeholder="Insurance notes (optional)"
              value={form.insurance_other_notes}
              onChange={handleFormChange("insurance_other_notes")}
              disabled={busy}
            />

            <select
              className="cp-select"
              value={form.gender_preference}
              onChange={handleFormChange("gender_preference")}
              disabled={busy}
            >
              {GENDER_PREFS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>

            <input
              className="cp-input"
              placeholder="Availability notes (optional)"
              value={form.availability_notes}
              onChange={handleFormChange("availability_notes")}
              disabled={busy}
            />

            <label
              className="cp-note"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 12px",
                borderRadius: 14,
              }}
            >
              <input
                type="checkbox"
                checked={form.sliding_scale_ok}
                onChange={handleFormChange("sliding_scale_ok")}
                disabled={busy}
              />
              Sliding scale OK
            </label>

            <label
              className="cp-note"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 12px",
                borderRadius: 14,
              }}
            >
              <input
                type="checkbox"
                checked={form.insurance_verified}
                onChange={handleFormChange("insurance_verified")}
                disabled={busy}
              />
              Insurance verified
            </label>

            <input
              className="cp-input"
              placeholder="Insurance verification notes (optional)"
              value={form.insurance_verified_notes}
              onChange={handleFormChange("insurance_verified_notes")}
              disabled={busy || !form.insurance_verified}
            />

            <button
              className="cp-btn primary"
              style={primaryBtnStyle}
              type="submit"
              disabled={busy}
            >
              {busy ? "Saving…" : "Add client"}
            </button>
          </form>

          <div className="cp-section" style={{ marginTop: 12 }}>
            <div className="cp-sectionTitle">Presenting concerns</div>
            <textarea
              className="cp-textarea"
              placeholder="Notes (optional)"
              value={form.presenting_concerns}
              onChange={handleFormChange("presenting_concerns")}
              disabled={busy}
            />
          </div>
        </div>

        {/* Clients table */}
        <div className="cp-card">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "center",
              marginBottom: 10,
              flexWrap: "wrap",
            }}
          >
            <h3>Clients</h3>
            <div className="cp-pill">Total: {_clients.length}</div>
          </div>

          <div className="cp-tableWrap">
            <table className="cp-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Insurance</th>
                  <th>Modality</th>
                  <th>Location</th>
                  <th>Assigned</th>
                  <th>Verified</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {_clients.map((c) => {
                  const a = getAssignmentForClient(_assignments, c.id);
                  const cl = a?.clinician_id
                    ? _clinicians.find((x) => x.id === a.clinician_id)
                    : null;

                  const isSelected = c.id === selectedId;

                  return (
                    <tr
                      key={c.id}
                      style={{
                        cursor: "pointer",
                        background: isSelected ? "rgba(29,78,216,.06)" : "",
                      }}
                      onClick={() => setSelectedId(c.id)}
                    >
                      <td style={{ fontWeight: 800 }}>
                        {(c.first_name || "")} {(c.last_name || "")}
                      </td>
                      <td>{c.insurance || "—"}</td>
                      <td>{c.preferred_modality || "—"}</td>
                      <td>{c.preferred_location || "—"}</td>
                      <td>{cl ? clinicianLabel(cl) : "—"}</td>
                      <td>
                        {c.insurance_verified ? (
                          <span
                            className="cp-chip"
                            style={{ background: "var(--success)" }}
                          >
                            Yes
                          </span>
                        ) : (
                          <span className="cp-chip" style={{ background: "#64748b" }}>
                            No
                          </span>
                        )}
                      </td>
                      <td>{fmtDate(c.created_at)}</td>
                    </tr>
                  );
                })}
                {_clients.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: 14, color: "rgba(36,64,52,.75)" }}>
                      No clients yet. Add one above.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        {/* Selected client details */}
        <div className="cp-card" style={{ marginTop: 14 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div>
              <h3>Selected client</h3>
              <div style={{ fontWeight: 800, marginTop: 4 }}>
                {selected
                  ? `${selected.first_name || ""} ${selected.last_name || ""}`.trim()
                  : "None"}
              </div>
            </div>

            <div className="cp-actions">
              <button
                className="cp-btn danger"
                style={dangerBtnStyle}
                onClick={deleteSelected}
                disabled={busy || !selected}
              >
                Delete
              </button>
            </div>
          </div>

          {!selected ? (
            <div className="cp-note" style={{ marginTop: 12 }}>
              Select a client from the table to view / edit details.
            </div>
          ) : (
            <>
              <div className="cp-section" style={{ marginTop: 12 }}>
                <div className="cp-sectionTitle">Assignment</div>

                <div className="cp-sectionPanel">
                  <div style={{ display: "grid", gap: 10 }}>
                    <div>
                      <div style={{ fontWeight: 800, marginBottom: 6 }}>
                        Assigned clinician
                      </div>
                      <select
                        className="cp-select"
                        value={assignment?.clinician_id || ""}
                        onChange={onAssignChange}
                        disabled={busy}
                      >
                        <option value="">— Unassigned —</option>
                        {_clinicians.map((cl) => (
                          <option key={cl.id} value={cl.id}>
                            {clinicianLabel(cl)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="cp-note">
                      Current:{" "}
                      <span style={{ fontWeight: 900 }}>
                        {assignedClinician
                          ? clinicianLabel(assignedClinician)
                          : "Unassigned"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="cp-section">
                <div className="cp-sectionTitle">Client notes</div>

                <div className="cp-sectionPanel" style={{ display: "grid", gap: 10 }}>
                  <div style={{ display: "grid", gap: 10 }}>
                    <div style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 800 }}>Presenting concerns</div>
                      <textarea
                        className="cp-textarea"
                        value={selected.presenting_concerns || ""}
                        onChange={(e) =>
                          updateSelected({ presenting_concerns: e.target.value })
                        }
                        disabled={busy}
                      />
                    </div>

                    <div style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontWeight: 800 }}>Availability notes</div>
                      <textarea
                        className="cp-textarea"
                        value={selected.availability_notes || ""}
                        onChange={(e) =>
                          updateSelected({ availability_notes: e.target.value })
                        }
                        disabled={busy}
                      />
                    </div>

                    <div className="cp-note">
                      Insurance verified at: <b>{fmtDate(selected.insurance_verified_at)}</b>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="cp-footer">
          <div className="cp-footerInner">
            <img className="cp-footerLogo" src="/carepathwell.png" alt="CarePathWell" />
            <div className="cp-footerText">
              <div className="cp-footerLine">
                Tip: For sign-in, use <b>carepathwell.com</b>.
              </div>
              <div className="cp-footerSub">
                © {new Date().getFullYear()} CarePathWell. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
